import React, { memo, useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Theme } from '@app/theme';
import { Trans } from 'react-i18next';
import {
  ColumnInstance,
  UseFiltersColumnProps,
  UseSortByColumnProps,
} from 'react-table';
import { MenuList, Popover, PopoverOrigin } from '@mui/material';
import { MenuItem } from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  headerCell: {
    ...theme.fonts.label,
    textAlign: 'left',
    textTransform: 'uppercase',
    color: theme.colors.inkTertiary,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.basicColors.grayBackground.fade(0.3).toString(),
    },
  },
  cellContent: {
    display: 'flex',
    alignItems: 'center',
    height: theme.height.large,

    textAlign: 'left',
    paddingLeft: 0,
    paddingRight: theme.marginBase,
    '& .checkbox-label': {
      height: theme.height.large,
    },
  },
  cellContentCentered: {
    justifyContent: 'center',
    width: '100%',
    minWidth: theme.height.small,
    paddingLeft: theme.marginBase,
    textAlign: 'center',
  },
  sortIcon: {
    marginLeft: theme.marginBase / 2,
    fontSize: theme.iconSize.regular,
  },
  filterIcon: {
    marginLeft: theme.marginBase / 2,
    fontSize: theme.iconSize.regular,
    color: theme.colors.blueAccessible,
  },
  paper: {
    width: theme.maxContentWidth.mediumBlock,
    '&.MuiPopover-paper': {
      overflow: 'visible',
    },
  },
}));

type Nullable<T> = { [P in keyof T]?: T[P] };

type TableColumn<D extends object> = ColumnInstance<D> &
  Nullable<UseSortByColumnProps<D>> &
  Nullable<UseFiltersColumnProps<D>>;

export interface Column<D extends object> extends TableColumn<D> {
  notOrderable?: boolean;
  centered?: boolean;
  singleLine?: boolean;
}

interface Props {
  // column: Column<D>;
  column: any;
  children?: React.ReactNode;
}

const popoverAnchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};
const popoverTransformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'left',
};

export const HeadCell = memo(({ column, children }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [anchorEl, setAnchorEl] =
    React.useState<HTMLTableHeaderCellElement | null>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLTableHeaderCellElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl],
  );
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const onSortAscClick = useCallback(() => {
    column.toggleSortBy && column.toggleSortBy(false, false);
    handleClose();
  }, [handleClose, column]);

  const onSortDescClick = useCallback(() => {
    column.toggleSortBy && column.toggleSortBy(true, false);
    handleClose();
  }, [handleClose, column]);

  return (
    <>
      <th
        {...column.getHeaderProps()}
        className={classes.headerCell}
        onClick={handleClick}
      >
        <button
          className={classnames(classes.cellContent, {
            [classes.cellContentCentered]: column.centered,
          })}
        >
          {children}
          {!column.notOrderable &&
            !!column.isSorted &&
            (column.isSortedDesc ? (
              <i
                className={classnames(classes.sortIcon, 'ri-arrow-down-s-line')}
              />
            ) : (
              <i
                className={classnames(classes.sortIcon, 'ri-arrow-up-s-line')}
              />
            ))}
          {column.filterValue !== undefined && (
            <i className={classnames('ri-filter-line', classes.filterIcon)} />
          )}
        </button>
      </th>
      {(!column.notOrderable || column.canFilter) && (
        <Popover
          id={column.id}
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={popoverAnchorOrigin}
          transformOrigin={popoverTransformOrigin}
          classes={{
            paper: classes.paper,
          }}
        >
          {column.toggleSortBy && !column.notOrderable && (
            <MenuList>
              <MenuItem onClick={onSortAscClick}>
                <Trans i18nKey="participants.list.filters.sortAsc" />
              </MenuItem>
              <MenuItem onClick={onSortDescClick}>
                <Trans i18nKey="participants.list.filters.sortDesc" />
              </MenuItem>
            </MenuList>
          )}
          {column.canFilter && column.render('Filter')}
        </Popover>
      )}
    </>
  );
});
